import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

type HashAccordionOptions = {
    /**
     * What a record is called when a link names it rather than numbering it.
     * Links written before the lists were rebuilt point at names —
     * /browse_services/#database — and go on arriving from bookmarks, tickets
     * and the search bar, so a list that has names opens from either.
     */
    aliasOf?: (record: any) => string | undefined;
};

/** one token, comparable: ids and names are matched the same way */
const key = (value: unknown) => String(value ?? '').trim().toLowerCase();

/** a row on a page: a toggle's, or a location's */
const rowOn = ({ pathname, hash }: { pathname: string; hash: string }) => `${pathname}#${key(decodeHash(hash))}`;

/**
 * The locations a row's own toggle navigated to — and Back and Forward to.
 *
 * A hash that arrives otherwise is a link — from a notification, the search
 * bar, a bookmark — and is followed: the row scrolled to, and pinned
 * (useLinkedPin), which starts the list over with that row first. One the
 * reader wrote by clicking a row is neither. The location objects are noted
 * as they arrive, so a list that renders one late — after react-admin has
 * already pushed the next page into the URL — still reads it as the click it
 * was.
 */
const ownLocations = new WeakSet<object>();

/**
 * Which history entries a toggle made, for Back and Forward: the entry, not
 * the row — a link to a row the reader once clicked is still a link, and
 * going back to it pins it again. The router's own state cannot carry this:
 * the app's hash history drops it (createHashHistory does not keep state). The
 * browser's entry has state of its own, which hash history never touches, and
 * which travels with the entry through Back and Forward; the toggle marks it.
 */
const TOGGLE_MARK = 'netorcaRowToggle';

const markEntry = () => {
    try {
        window.history.replaceState({ ...(window.history.state || {}), [TOGGLE_MARK]: true }, '');
    } catch {
        // a history that refuses state: Back to the click reads as a link
    }
};

const entryMarked = () => {
    try {
        return !!window.history.state?.[TOGGLE_MARK];
    } catch {
        return false;
    }
};

/** the toggle being pushed, until its own push is heard */
let pushing: string | null = null;

/** the history objects already watched — one listener each, for good */
const watched = new WeakSet<object>();

const watch = (history: { listen: (listener: (location: any, action: string) => void) => unknown }) => {
    if (watched.has(history)) return;
    watched.add(history);
    history.listen((location, action) => {
        // Back and Forward land on an entry, which says whose it is
        if (action === 'POP') {
            if (entryMarked()) ownLocations.add(location);
            return;
        }
        if (pushing === rowOn(location)) ownLocations.add(location);
        pushing = null;
    });
};

/** this location is one a row's toggle navigated to, not a link */
export const isRowToggle = (location: object) => ownLocations.has(location);

/** a hash carries whatever a name carries, so it travels encoded */
export const decodeHash = (hash: string) => {
    const raw = hash.replace(/^#/, '');
    try {
        return decodeURIComponent(raw);
    } catch {
        // a half-written escape is not a row, but it is not a crash either
        return raw;
    }
};

/**
 * One row open at a time, named in the URL hash.
 *
 * The open row is addressable: it survives a reload and can be linked to from
 * elsewhere — a notification about a rejected change, say. Only a hash *being
 * set* opens a row, because react-admin rewrites the search string whenever
 * list params change, which drops the hash, and that must not collapse what the
 * reader is in the middle of. Closing is driven by toggle().
 *
 * The hash names a row by id, or by whatever `aliasOf` calls it. Both open the
 * same row whichever the list writes, so a link that has been sitting in
 * someone's notes for two years still lands where it always did.
 *
 * `scrollsTo` is the row an incoming link asked for. Hand it to that row and
 * let it scroll itself once it has rendered: a list cannot know when its rows
 * reach the DOM, and a row that is not in the result set simply never scrolls.
 *
 * Pages arrive on scroll, so the row a link names may not be on the first one.
 * The list does not go looking for it: the API puts it first — see
 * useLinkedPin, which turns the same link into the list's `pin` filter.
 */
const useHashAccordion = ({ aliasOf }: HashAccordionOptions = {}) => {
    const location = useLocation();
    const history = useHistory();
    watch(history);

    const [openKey, setOpenKey] = useState<string | null>(null);
    const [scrollKey, setScrollKey] = useState<string | null>(null);

    useEffect(() => {
        const token = key(decodeHash(location.hash));
        if (token) {
            setOpenKey(token);
            // a click already put the row under the cursor — only an incoming
            // link needs the list scrolled
            if (!isRowToggle(location)) setScrollKey(token);
        }
    }, [location.hash]);

    /** what this record is called in a link */
    const linkName = (record: any) => (aliasOf && aliasOf(record)) || String(record?.id ?? '');

    const named = (record: any, token: string | null) => !!token && (
        key(record?.id) === token || key(aliasOf && aliasOf(record)) === token
    );

    const toggle = (record: any) => {
        const name = linkName(record);
        const next = named(record, openKey) ? null : key(name);
        setOpenKey(next);
        // whatever the link asked for, the reader has moved on from it
        setScrollKey(null);
        // keep the open row addressable — marked as the reader's own, so it
        // does not read back as a link
        const hash = next ? `#${encodeURIComponent(name)}` : '';
        pushing = rowOn({ pathname: location.pathname, hash });
        history.push(`${location.pathname}${location.search}${hash}`);
        // the entry the push made, marked as the reader's own for Back and
        // Forward to read: hash history sets the URL, and so makes the entry,
        // before it returns
        markEntry();
    };

    return {
        isOpen: (record: any) => named(record, openKey),
        scrollsTo: (record: any) => named(record, scrollKey),
        toggle,
    };
};

export default useHashAccordion;
